import { ArrowBack, Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  ListItemButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import StudentReportDetails from "./StudentReportDetails";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  closeStudentActivity,
  fetchStudentReport,
} from "../store/studentsSlice";

import type { StudentReportOptions } from "@lingoshade/domain";

function ReportContent({ id }: { id: string }): React.ReactElement {
  const { t, i18n } = useTranslation("texts");
  const dispatch = useAppDispatch();
  const {
    report,
    reportBusy: busy,
    reportError: error,
    teacher,
  } = useAppSelector((state) => state.students);
  const [days, setDays] = useState<0 | 7 | 30 | 90>(30);
  const [textId, setTextId] = useState<string | undefined>();
  const [section, setSection] = useState<"tasks" | "vocabulary">("tasks");
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const load = (options: StudentReportOptions): void => {
    void dispatch(fetchStudentReport({ id, options }));
  };
  const minutes = (seconds: number): string =>
    t("students.report.minutes", { count: Math.round(seconds / 60) });
  const accuracy = (value: number | null): string =>
    value === null ? "—" : `${value.toFixed(1)}%`;
  const date = (value: string): string =>
    new Date(value).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
    });
  const selected = textId
    ? report?.tasks.find((entry): boolean => entry.textId === textId)
    : undefined;
  const isDetails = Boolean(selected) || section === "vocabulary";
  const tasks =
    report?.tasks.filter(
      (entry): boolean =>
        (entry.title || "")
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase().trim()) &&
        (filter === "all" ||
          (filter === "idle"
            ? !entry.lastActiveAt
            : Boolean(entry.lastActiveAt))),
    ) || [];
  const student = teacher.relationships.find(
    (entry): boolean => entry.id === id,
  );
  const openTask = (value: string): void => {
    setTextId(value);
    setSection("tasks");
    setOffset(0);
    load({ days, textId: value });
  };
  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Box>
            <Typography component="span" variant="h6">
              {t("students.report.title")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflowWrap: "anywhere" }}
            >
              {student?.email}
            </Typography>
          </Box>
          <IconButton
            aria-label={t("students.close")}
            onClick={(): void => {
              dispatch(closeStudentActivity());
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ my: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            {t(
              report?.fullActivity
                ? "students.report.allScope"
                : "students.report.assignedScope",
            )}
          </Typography>
          <TextField
            select
            size="small"
            label={t("students.report.period")}
            value={days}
            disabled={busy}
            sx={{ minWidth: 150 }}
            onChange={(event): void => {
              const value = Number(event.target.value) as 0 | 7 | 30 | 90;
              setDays(value);
              setOffset(0);
              setPage(0);
              load({ days: value, textId, section });
            }}
          >
            {[7, 30, 90, 0].map(
              (value): React.ReactElement => (
                <MenuItem key={value} value={value}>
                  {t(`students.report.period${value}`)}
                </MenuItem>
              ),
            )}
          </TextField>
        </Stack>
        {busy && <LinearProgress sx={{ my: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {t(`students.errors.${error}`, {
              defaultValue: t("students.errors.STUDENT_TECHNICAL_ERROR"),
            })}
            <Button
              onClick={(): void => load({ days, textId, section, offset })}
            >
              {t("students.report.retry")}
            </Button>
          </Alert>
        )}
        {report && (
          <>
            {!isDetails && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(4, 1fr)",
                    },
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    my: 2,
                  }}
                >
                  {[
                    [
                      minutes(report.summary.practiceSeconds),
                      t("students.practice"),
                    ],
                    [
                      String(report.summary.completedDictations),
                      t("students.report.completedDictations"),
                    ],
                    [
                      String(report.summary.wordsAdded),
                      t("students.report.savedWords"),
                    ],
                    [
                      String(report.summary.activeDays),
                      t("students.report.activeDays"),
                    ],
                  ].map(
                    ([value, label]): React.ReactElement => (
                      <Box key={label} sx={{ p: 1.5 }}>
                        <Typography variant="h6">{value}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("students.report.reviewsSummary", {
                      count: report.summary.wordReviews,
                      correct: report.summary.correctReviews,
                    })}
                  </Typography>
                  <Button
                    size="small"
                    onClick={(): void => {
                      setSection("vocabulary");
                      setOffset(0);
                      load({ days, section: "vocabulary" });
                    }}
                  >
                    {t("students.report.vocabularyDetails")}
                  </Button>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <TextField
                    size="small"
                    label={t("students.report.search")}
                    value={search}
                    onChange={(event): void => {
                      setSearch(event.target.value);
                      setPage(0);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    label={t("students.filter")}
                    value={filter}
                    onChange={(event): void => {
                      setFilter(event.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    {["all", "active", "idle"].map(
                      (value): React.ReactElement => (
                        <MenuItem key={value} value={value}>
                          {t(`students.report.filter${value}`)}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                </Stack>
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  {tasks.slice(page * 10, page * 10 + 10).map(
                    (entry): React.ReactElement => (
                      <ListItemButton
                        key={entry.textId}
                        onClick={(): void => openTask(entry.textId)}
                        sx={{
                          px: 0,
                          py: 1,
                          display: "block",
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="primary.main"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {entry.title || t("students.report.untitled")}
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {minutes(entry.practiceSeconds)} ·{" "}
                            {t("students.dictations")}:{" "}
                            {entry.completedDictations}
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {accuracy(entry.latestAccuracy)}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {entry.lastActiveAt
                            ? date(entry.lastActiveAt)
                            : t("students.report.notStarted")}{" "}
                          · {t("students.report.savedWords")}:{" "}
                          {entry.wordsAdded}
                        </Typography>
                      </ListItemButton>
                    ),
                  )}
                </Box>
                <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
                  <Table
                    size="small"
                    aria-label={t("students.report.tasks")}
                    sx={{ "& .MuiTableCell-root": { py: 0.75 } }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("students.report.tasks")}</TableCell>
                        <TableCell align="right">
                          {t("students.practice")}
                        </TableCell>
                        <TableCell align="right">
                          {t("students.dictations")}
                        </TableCell>
                        <TableCell align="right">
                          {t("students.report.latestBest")}
                        </TableCell>
                        <TableCell align="right">
                          {t("students.report.savedWords")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.slice(page * 10, page * 10 + 10).map(
                        (entry): React.ReactElement => (
                          <TableRow key={entry.textId} hover>
                            <TableCell sx={{ minWidth: 150, maxWidth: 300 }}>
                              <Button
                                size="small"
                                sx={{
                                  textAlign: "left",
                                  justifyContent: "flex-start",
                                  p: 0,
                                  minHeight: 24,
                                  textTransform: "none",
                                }}
                                onClick={(): void => openTask(entry.textId)}
                              >
                                {entry.title || t("students.report.untitled")}
                              </Button>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                {entry.lastActiveAt
                                  ? date(entry.lastActiveAt)
                                  : t("students.report.notStarted")}
                                {!entry.assigned &&
                                  ` · ${t("students.report.otherMaterial")}`}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {minutes(entry.practiceSeconds)}
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                {t("students.report.sessions", {
                                  count: entry.practiceCount,
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {entry.completedDictations}
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                {entry.dictationCount >
                                entry.completedDictations
                                  ? t("students.report.unfinished", {
                                      count:
                                        entry.dictationCount -
                                        entry.completedDictations,
                                    })
                                  : ""}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              <Typography variant="body2">
                                {accuracy(entry.latestAccuracy)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {accuracy(entry.bestAccuracy)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {entry.wordsAdded}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!tasks.length && (
                  <Typography
                    color="text.secondary"
                    sx={{ py: 3, textAlign: "center" }}
                  >
                    {t("students.report.emptyTasks")}
                  </Typography>
                )}
                {tasks.length > 10 && (
                  <TablePagination
                    component="div"
                    count={tasks.length}
                    page={page}
                    rowsPerPage={10}
                    rowsPerPageOptions={[10]}
                    onPageChange={(_, value): void => setPage(value)}
                    labelDisplayedRows={({ from, to, count }): string =>
                      t("students.report.pagination", { from, to, count })
                    }
                    getItemAriaLabel={(type): string =>
                      t(`students.report.page${type}`)
                    }
                  />
                )}
              </>
            )}
            {isDetails && (
              <>
                <Button
                  startIcon={<ArrowBack />}
                  sx={{ mt: 1, mb: 2 }}
                  onClick={(): void => {
                    setTextId(undefined);
                    setSection("tasks");
                    setOffset(0);
                    load({ days });
                  }}
                >
                  {t("students.report.back")}
                </Button>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {section === "vocabulary"
                    ? t("students.words")
                    : selected?.title || t("students.report.untitled")}
                </Typography>
                {selected && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t("students.report.progress", {
                      first: accuracy(selected.firstAccuracy),
                      latest: accuracy(selected.latestAccuracy),
                      best: accuracy(selected.bestAccuracy),
                    })}
                  </Typography>
                )}
                {report.details && (
                  <StudentReportDetails details={report.details} />
                )}
                {(offset > 0 || report.details?.hasMore) && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      disabled={busy || offset === 0}
                      onClick={(): void => {
                        const value = Math.max(0, offset - 10);
                        setOffset(value);
                        load({ days, textId, section, offset: value });
                      }}
                    >
                      {t("students.report.newer")}
                    </Button>
                    <Button
                      disabled={busy || !report.details?.hasMore}
                      onClick={(): void => {
                        const value = offset + 10;
                        setOffset(value);
                        load({ days, textId, section, offset: value });
                      }}
                    >
                      {t("students.report.older")}
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </>
  );
}
export default function StudentActivityDialog(): React.ReactElement {
  const dispatch = useAppDispatch();
  const id = useAppSelector((state) => state.students.activityId);
  return (
    <Dialog
      open={Boolean(id)}
      fullWidth
      maxWidth="lg"
      onClose={(): void => {
        dispatch(closeStudentActivity());
      }}
    >
      {id && <ReportContent key={id} id={id} />}
    </Dialog>
  );
}
